import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  type LastMinuteTier,
  type LastMinuteParams,
  type RuleFormProps,
  pctStrToNumOpt,
  intStrOpt,
} from '@/lib/pricingRules';

// last_minute: tiered lead-time discount/markup. Each tier fires when the
// booking's lead time (days between booking and arrival) is <= within_days;
// the TIGHTEST matching tier wins (smallest within_days that still covers the
// lead time). Mirrors the entry/tier-list shape from DateSpecificRuleForm
// (the reference form): local string-draft state seeded once from params,
// rebuilt + emitted on every change.
//
// within_days and pct are REQUIRED by the backend (no omitempty) — blank/
// invalid input falls back rather than being omitted: within_days keeps the
// last valid value (or 1), pct coerces to 0. min_stay is optional and is
// ALWAYS emitted explicitly (`null` when blank — the backend's `*int` field
// has no omitempty and expects the key present).

interface TierDraft {
  withinDays: string;
  withinDaysNum: number; // last-known-valid within_days (>= 1); used on emit
  pct: string;
  minStay: string;
}

function tierToDraft(t: LastMinuteTier): TierDraft {
  return {
    withinDays: String(t.within_days),
    withinDaysNum: t.within_days,
    pct: String(t.pct),
    minStay: t.min_stay != null ? String(t.min_stay) : '',
  };
}

function draftToTier(d: TierDraft): LastMinuteTier {
  const p = pctStrToNumOpt(d.pct);
  const pct = typeof p === 'number' ? p : 0;
  const ms = intStrOpt(d.minStay);
  const min_stay = typeof ms === 'number' ? ms : null;
  return { within_days: d.withinDaysNum, pct, min_stay };
}

export function LastMinuteRuleForm({ params, onChange }: RuleFormProps<LastMinuteParams>) {
  const [drafts, setDrafts] = useState<TierDraft[]>(() => (params.tiers ?? []).map(tierToDraft));

  function commit(next: TierDraft[]) {
    setDrafts(next);
    onChange({ tiers: next.map(draftToTier) });
  }

  function patch(i: number, p: Partial<TierDraft>) {
    commit(drafts.map((d, idx) => (idx === i ? { ...d, ...p } : d)));
  }

  function patchWithinDays(i: number, value: string) {
    const parsed = intStrOpt(value);
    commit(
      drafts.map((d, idx) => {
        if (idx !== i) return d;
        const num = typeof parsed === 'number' && parsed >= 1 ? parsed : d.withinDaysNum;
        return { ...d, withinDays: value, withinDaysNum: num };
      }),
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {drafts.map((d, i) => (
        <div
          key={i}
          className="rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-3"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label="Within N days of arrival"
              type="number"
              min={1}
              value={d.withinDays}
              onChange={(e) => patchWithinDays(i, e.target.value)}
              hint="Tightest matching tier wins."
            />
            <Input
              label="Adjustment (%)"
              type="number"
              step="0.1"
              value={d.pct}
              onChange={(e) => patch(i, { pct: e.target.value })}
              placeholder="e.g. -35"
            />
            <Input
              label="Min stay (nights)"
              type="number"
              min={1}
              value={d.minStay}
              onChange={(e) => patch(i, { minStay: e.target.value })}
              placeholder="optional"
            />
          </div>

          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => commit(drafts.filter((_, idx) => idx !== i))}
            >
              <Trash2 className="h-4 w-4" />
              Remove tier
            </Button>
          </div>
        </div>
      ))}

      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() =>
            commit([...drafts, { withinDays: '7', withinDaysNum: 7, pct: '0', minStay: '' }])
          }
        >
          <Plus className="h-4 w-4" />
          Add tier
        </Button>
      </div>
    </div>
  );
}
